/**
 * El control de la foto: subir, reemplazar, quitar.
 *
 * Es el mismo para un producto y para el logo, porque la operacion es la misma
 * y solo cambia el lado al que se achica. Lo que ve la persona es una
 * miniatura; lo que hay abajo es un `<input type="file">` escondido, que en el
 * celular abre la camara o la galeria segun elija.
 *
 * Nada de esto tiene «Guardar»: se elige la foto y se sube. Un boton de guardar
 * para una imagen que ya se ve en pantalla es un paso que solo sirve para
 * olvidarse de darlo.
 */
import { useRef, useState, type DragEvent } from "react";
import { Camera, ImageOff, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/cargando";
import { mostrarError } from "@/lib/errores";
import { cn } from "@/lib/utils";

import { achicarImagen, formularioDeImagen, LADO_PRODUCTO } from "./logica";

export interface BotonFotoProps {
  /**
   * La URL ya resuelta de la foto que hay hoy, o `null` si no tiene.
   *
   * Resuelta por quien llama y no aca: un producto se pide por la ruta con
   * sesion (para poder ver lo que todavia no se publico) y el logo por la
   * publica. Ver `srcImagenAdmin` y `srcImagen`.
   */
  src: string | null;
  /** Nombre de lo que se esta fotografiando, para el texto accesible. */
  nombre: string;
  onSubir: (formulario: FormData) => Promise<unknown>;
  onQuitar: () => Promise<unknown>;
  /** Lado mayor al que se achica. Los productos a 1200, el logo a 400. */
  lado?: number;
  /** `chico` es la miniatura de una fila de tabla; `grande`, el logo. */
  tamano?: "chico" | "grande";
  deshabilitado?: boolean;
}

export function BotonFoto({
  src,
  nombre,
  onSubir,
  onQuitar,
  lado = LADO_PRODUCTO,
  tamano = "chico",
  deshabilitado = false,
}: BotonFotoProps): JSX.Element {
  const entrada = useRef<HTMLInputElement>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [encima, setEncima] = useState(false);

  const foto = src;
  const medida = tamano === "grande" ? "size-16" : "size-11";

  async function procesar(archivo: File | undefined): Promise<void> {
    if (!archivo || deshabilitado) return;
    setTrabajando(true);
    try {
      const achicada = await achicarImagen(archivo, lado);
      await onSubir(formularioDeImagen(achicada));
    } catch (error) {
      mostrarError(error);
    } finally {
      setTrabajando(false);
      // Sin esto, elegir DOS VECES el mismo archivo no dispara `change`: el
      // valor del input no cambio. Pasa justo cuando alguien reintenta despues
      // de un error.
      if (entrada.current) entrada.current.value = "";
    }
  }

  async function quitar(): Promise<void> {
    setTrabajando(true);
    try {
      await onQuitar();
    } catch (error) {
      mostrarError(error);
    } finally {
      setTrabajando(false);
    }
  }

  function alSoltar(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setEncima(false);
    void procesar(e.dataTransfer.files[0]);
  }

  return (
    <div className="flex items-center gap-2">
      {/* Input crudo y escondido: no hay primitivo de archivo, y el selector
          nativo es justamente lo que en el celular ofrece la camara. Lo que se
          toca es el boton de abajo. */}
      <input
        ref={entrada}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => void procesar(e.target.files?.[0])}
      />

      {/* El arrastrar y soltar es para la PC, donde se carga el catalogo de
          entrada. En el celular el mismo boton abre la camara. */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={alSoltar}
      >
        {/* Boton crudo: lo que se ve es la miniatura, no un boton. `Button`
            trae padding, alto y fondo propios que hay que pisar enteros. */}
        <button
          type="button"
          disabled={deshabilitado || trabajando}
          onClick={() => entrada.current?.click()}
          aria-label={foto ? `Cambiar la foto de ${nombre}` : `Agregar una foto a ${nombre}`}
          className={cn(
            "flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-dashed border-border bg-muted text-muted-foreground transition-colors",
            "hover:border-ring hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-60",
            foto && "border-solid",
            encima && "border-primary text-primary",
            medida,
          )}
        >
          {trabajando ? (
            <Spinner />
          ) : foto ? (
            <img src={foto} alt="" className="size-full object-cover" />
          ) : tamano === "grande" ? (
            <Upload className="size-5" aria-hidden="true" />
          ) : (
            <Camera className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {foto && !trabajando ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={deshabilitado}
          onClick={() => void quitar()}
          aria-label={`Quitar la foto de ${nombre}`}
        >
          <ImageOff aria-hidden="true" />
          <span className="sr-only sm:not-sr-only">Quitar</span>
        </Button>
      ) : null}
    </div>
  );
}
